import type { TVChannel } from '@/types'
import { debugError } from '@/utils/env'
import type { ApiResponse } from './types'

// API基础URL
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api'

// 频道信息接口
export interface TVChannelInfo {
  id: number
  name: string
  code: string
  description: string
  color: string
  created_at: string
  documentary_count?: number
  comment_count?: number,
  logo?: string,
}

/**
 * 获取所有频道列表
 */
export async function getTVChannels(): Promise<TVChannelInfo[]> {
  try {
    const response = await fetch(`${API_BASE_URL}/tvchannels`)
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    
    const result: ApiResponse<TVChannelInfo[]> = await response.json()

    if (result.errorcode === '0') {
      return result.data
    } else {
      throw new Error(result.errormsg || '获取频道列表失败')
    }
  } catch (error) {
    debugError('Failed to fetch channels:', error)
    return getDefaultChannels()
  }
}

/**
 * 根据code获取单个频道信息
 */
export async function getChannelByCode(code: string): Promise<TVChannelInfo | null> {
  try {
    const response = await fetch(`${API_BASE_URL}/tvchannels/${code}`)

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const result: ApiResponse<TVChannelInfo> = await response.json()

    if (result.errorcode === '0') {
      return result.data
    } else {
      throw new Error(result.errormsg || '获取频道信息失败')
    }
  } catch (error) {
    debugError('Failed to fetch channel:', error)
    return null
  }
}

/**
 * 获取频道下的纪录片列表
 */
export async function getChannelDocumentaries(
  code: string,
  page: number = 1,
  limit: number = 20
): Promise<{ documentaries: any[], total: number }> {
  try {
    const response = await fetch(
      `${API_BASE_URL}/tvchannels/${code}/documentaries?page=${page}&limit=${limit}`
    )

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const result: ApiResponse<{ documentaries: any[], total: number }> = await response.json()

    if (result.errorcode === '0') {
      return result.data
    } else {
      throw new Error(result.errormsg || '获取频道纪录片失败')
    }
  } catch (error) {
    debugError('Failed to fetch channel documentaries:', error)
    return { documentaries: [], total: 0 }
  }
}

/**
 * 默认频道数据（降级方案）
 */
export function getDefaultChannels(): TVChannelInfo[] {
  return [
    {
      id: 1,
      name: 'BBC',
      code: 'bbc',
      description: '英国广播公司，世界顶级纪录片制作机构',
      color: '#8B0000',
      created_at: new Date().toISOString(),
      documentary_count: 0,
      comment_count: 0
    },
    {
      id: 2,
      name: 'NHK',
      code: 'nhk',
      description: '日本放送协会，亚洲最具影响力的媒体之一',
      color: '#BC002D',
      created_at: new Date().toISOString(),
      documentary_count: 0,
      comment_count: 0
    },
    {
      id: 3,
      name: 'CCTV',
      code: 'cctv',
      description: '中国中央电视台，中国最具权威的媒体平台',
      color: '#CF0A2C',
      created_at: new Date().toISOString(),
      documentary_count: 0,
      comment_count: 0
    },
    {
      id: 4,
      name: 'Discovery',
      code: 'discovery',
      description: '探索频道，全球领先的纪实娱乐品牌',
      color: '#00B3E0',
      created_at: new Date().toISOString(),
      documentary_count: 0,
      comment_count: 0
    },
    {
      id: 5,
      name: 'National Geographic',
      code: 'NG',
      description: '国家地理，探索自然与科学的权威品牌',
      color: '#FFCC00',
      created_at: new Date().toISOString(),
      documentary_count: 0,
      comment_count: 0
    },
    {
      id: 6,
      name: 'HBO',
      code: 'hbo',
      description: 'HBO纪录片，高品质纪实内容制作商',
      color: '#000000',
      created_at: new Date().toISOString(),
      documentary_count: 0,
      comment_count: 0
    },
    {
      id: 7,
      name: 'Netflix',
      code: 'netflix',
      description: 'Netflix原创纪录片，流媒体平台优质内容',
      color: '#E50914',
      created_at: new Date().toISOString(),
      documentary_count: 0,
      comment_count: 0
    },
    {
      id: 8,
      name: 'Amazon Prime',
      code: 'amazon',
      description: 'Amazon Prime Video纪录片，原创纪实内容',
      color: '#00A8E1',
      created_at: new Date().toISOString(),
      documentary_count: 0,
      comment_count: 0
    },
    {
      id: 9,
      name: 'Apple TV+',
      code: 'apple',
      description: 'Apple TV+原创纪录片，高质量纪实节目',
      color: '#000000',
      created_at: new Date().toISOString(),
      documentary_count: 0,
      comment_count: 0
    },
    {
      id: 10,
      name: 'YouTube',
      code: 'youtube',
      description: 'YouTube纪录片频道，用户生成和专业制作内容',
      color: '#FF0000',
      created_at: new Date().toISOString(),
      documentary_count: 0,
      comment_count: 0
    }
  ]
}

/**
 * 频道信息缓存
 */
let channelCache: TVChannelInfo[] | null = null
let cacheTimestamp: number = 0
const CACHE_DURATION = 5 * 60 * 1000 // 5分钟缓存

/**
 * 获取频道列表（带缓存和错误恢复）
 */
export async function getChannelsWithCache(): Promise<TVChannelInfo[]> {
  const now = Date.now()
  
  // 如果缓存存在且未过期，直接返回
  if (channelCache && channelCache.length > 0 && (now - cacheTimestamp) < CACHE_DURATION) {
    return channelCache
  }
  
  // 记录旧缓存用于错误恢复
  const oldCache = channelCache
  
  try {
    // 否则重新获取数据
    const channels = await getTVChannels()
    
    // 只有成功获取到有效数据才更新缓存
    if (channels && channels.length > 0) {
      channelCache = channels
      cacheTimestamp = now
    }
    
    return channels
  } catch (error) {
    debugError('获取频道数据失败:', error)
    
    // API 失败时，保留旧缓存或返回默认数据
    if (oldCache && oldCache.length > 0) {
      return oldCache
    }
    return getDefaultChannels()
  }
}

/**
 * 根据code获取频道名称
 */
export async function getChannelName(code: string): Promise<string> {
  const channels = await getChannelsWithCache()
  const channel = channels.find(c => c.code === code)
  return channel?.name || code.toUpperCase()
}

/**
 * 根据code获取频道描述
 */
export async function getChannelDescription(code: string): Promise<string> {
  const channels = await getChannelsWithCache()
  const channel = channels.find(c => c.code === code)
  return channel?.description || ''
}

/**
 * 根据code获取频道颜色
 */
export async function getChannelColor(code: string): Promise<string> {
  const channels = await getChannelsWithCache()
  const channel = channels.find(c => c.code === code)
  return channel?.color || '#666666'
}

/**
 * 获取所有频道的选项列表（用于下拉菜单等）
 */
export async function getChannelOptions(): Promise<{ value: TVChannel; label: string }[]> {
  const channels = await getChannelsWithCache()
  return channels.map(channel => ({
    value: channel.code as TVChannel,
    label: channel.name
  }))
}

/**
 * 清除频道缓存
 */
export function clearChannelCache(): void {
  channelCache = null
  cacheTimestamp = 0
}